import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, booleanAttribute, ChangeDetectorRef, Component, computed, effect, ElementRef, HostBinding, inject, Injector, input, OnInit, PLATFORM_ID, Signal, signal, ViewChild, } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faBackward, faCompress, faExpand, faForward, faPause, faPlay, faVolumeMute, faVolumeUp, } from '@fortawesome/free-solid-svg-icons';
import { VideoView } from 'src/app/@domain/video/entities/video-view.enum';
import { ImageDirective } from '../../../../@common/DOM/image.directive';
import { Logger } from '../../../../@common/log/logger';
import { SecondsToTimestampPipe } from '../../../../@common/pipe/seconds-to-timestamp.pipe';
import { NotificationService } from '../../../../@domain/notification/application/notification.service';
import { VideoService } from '../../../../@domain/video/application/video.service';
import { Subtitle, VideoPart } from '../../../../@generated';
import { SlideItem } from '../../content/sections/slide-item/slide-item.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { VideoViewPickerComponent } from './presentation-view/video-view-picker.component';
import { VideoPlayerSpeedsComponent } from './speeds/video-player-speeds.component';
import { VideoPlayerSubtitlesComponent } from './subtitles/video-player-subtitles.component';
import { VideoTimelineComponent } from './timeline-stamps/video-timeline.component';

@Component({
    selector: 'app-video-player',
    templateUrl: './video-player.component.html',
    styleUrls: [ './video-player.component.scss' ],
    imports: [
        VideoViewPickerComponent,
        ImageDirective,
        VideoTimelineComponent,
        FaIconComponent,
        FormsModule,
        VideoPlayerSubtitlesComponent,
        VideoPlayerSpeedsComponent,
        SpinnerComponent,
        SecondsToTimestampPipe
    ]
})
export class VideoPlayerComponent implements OnInit, AfterViewInit {
    private logger = inject(Logger);
    private videoService = inject(VideoService);
    private platformId = inject(PLATFORM_ID);
    private notificationService = inject(NotificationService);
    private cdr = inject(ChangeDetectorRef);
    private injector = inject(Injector);

    @ViewChild('video') public videoEl!: ElementRef;
    @ViewChild('timeline') public timeline!: ElementRef;
    @ViewChild('thumbIndicator') public thumbIndicator!: ElementRef;
    @ViewChild('timelineBuffered') public timelineBuffered!: ElementRef;
    @ViewChild('videoPresentationParent') public videoPresentationParent!: ElementRef;
    @HostBinding('class.manual-fullscreen') public manualFullscreen = false;
    public videoId = input.required<number>();
    public videoPart = input.required<VideoPart>();
    public slides = input.required<SlideItem[] | undefined>();
    public combinedPlayer = input(false, { transform: booleanAttribute })
    public syncView = input(false);
    public currentSlideImg: Signal<string | undefined>;
    public isFullScreenMode = signal(false);
    public volume = signal(100);
    public isVideoLoaded = signal(false);
    public activeView = signal<VideoView | null>(null);
    public currentSeconds = signal(0);
    public volumeBeforeMute = 0;
    public VideoView = VideoView;
    public faPlay = faPlay;
    public faForward = faForward;
    public faPause = faPause;
    public faVolumeUp = faVolumeUp;
    public faExpand = faExpand;
    public faCompress = faCompress;
    public faBackward = faBackward;
    public faVolumeMute = faVolumeMute;

    constructor() {
        this.currentSlideImg = computed(() => this.getPresentationImage(this.currentSeconds(), this.slides()));

        let oldSlides: SlideItem[] | undefined;
        effect(() => {
            const newSlides = this.slides();

            if (newSlides !== oldSlides) {
                this.activeView.set(
                    !this.combinedPlayer() && !!newSlides?.length && isPlatformBrowser(this.platformId) && window.innerWidth >= 600
                        ? VideoView.Split
                        : VideoView.Video
                );
                oldSlides = newSlides;
            }
        });
    }

    ngOnInit(): void {
        if (isPlatformBrowser(this.platformId) && !this.syncView()) {
            this.videoService.addView(this.videoId());
        }
    }

    ngAfterViewInit(): void {
        if (isPlatformBrowser(this.platformId)) {
            this.video?.addEventListener('error', (e) => {
                this.notificationService.error({ detail: 'Video failed to load. Please try again later.' })
                console.error('Video failed to load', (e.target as any).error);
            });

            effect(() => {
                const video = this.video;
                const part = this.videoPart();

                if (video) {
                    this.isVideoLoaded.set(false);
                    this.currentSecs = 0;
                    video.src = part.video_url!;
                    video.load();
                    this.cdr.detectChanges();
                }
            }, { injector: this.injector });

            effect(() => this.updateTimeLine(), { injector: this.injector });
        }
    }

    changeSlide({ timestamp }: SlideItem) {
        this.currentSecs = timestamp;
    }

    get video(): HTMLVideoElement | undefined {
        return this.videoEl?.nativeElement;
    }

    async toggleVideoPlayPause(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();

        if (this.isVideoLoaded()) {
            this.video?.paused ? await this.video.play() : this.video?.pause();
        }
    }

    getVideoPresentationParent(): HTMLDivElement | undefined {
        return this.videoPresentationParent?.nativeElement;
    }

    getTimeline(): HTMLDivElement | undefined {
        return this.timeline?.nativeElement;
    }

    getThumbIndicator(): HTMLDivElement | undefined {
        return this.thumbIndicator?.nativeElement;
    }

    toggleFullScreen(): void {
        if (this.manualFullscreen) {
            // IOS does not support element fullscreen
            this.manualFullscreen = false;
            this.isFullScreenMode.set(false);
            window.scroll(0, 0);
            document.getElementsByTagName('html')[0].style.overflowY = 'auto';
            document.getElementsByTagName('body')[0].style.background = 'unset';
            return;
        }

        const isFullScreen = document.fullscreenElement === null;
        try {
            if (!isFullScreen) {
                document.exitFullscreen()
            } else {
                const docEl = this.getVideoPresentationParent();
                // @ts-ignore
                if (docEl.requestFullscreen) docEl.requestFullscreen();
                // @ts-ignore
                else if (docEl.webkitRequestFullscreen) docEl.webkitRequestFullscreen();
                // @ts-ignore
                else if (docEl.mozRequestFullScreen) docEl.mozRequestFullScreen();
                // @ts-ignore
                else if (docEl.msRequestFullscreen) docEl.msRequestFullscreen();
                // @ts-ignore
                else if (docEl.webkitEnterFullscreen) docEl.webkitEnterFullscreen();
                else {
                    document.getElementsByTagName('html')[0].style.overflowY = 'hidden';
                    document.getElementsByTagName('body')[0].style.background = 'black';

                    this.manualFullscreen = true;
                    this.isFullScreenMode.set(true);
                    return;
                }
            }

            this.isFullScreenMode.set(isFullScreen);
        } catch (e: any) {
            this.logger.clientError('Failed to toggle fullscreen', e);
        }
    }

    toggleVolume(): void {
        const volume = this.volume();

        if (volume === 0) {
            this.volume.set(this.volumeBeforeMute);
        } else {
            this.volumeBeforeMute = volume;
            this.volume.set(0);
        }
    }

    public getPresentationImage(currentS: number, slides: SlideItem[] | undefined): string | undefined {
        if (!slides) {
            return undefined;
        }

        const prevSlides = slides.filter(slide => slide.timestamp <= currentS);
        return prevSlides[prevSlides.length - 1]?.image;
    }

    onChangeTimeline(event: MouseEvent): void {
        const timeline = this.getTimeline();

        if (!this.isVideoLoaded() || !timeline) {
            return;
        }

        const { x, width }: DOMRect = timeline.getBoundingClientRect();
        const percent: number = Math.min(Math.max(0, event.x - x), width) / width;
        const timeClicked: number = this.video!.duration * percent;

        if (!isNaN(timeClicked) && isFinite(timeClicked)) {
            this.currentSecs = timeClicked;
            this.updateTimeLine();
        }
    }

    onBufferChange(): void {
        const buffered = this.video?.buffered;
        const duration = this.video?.duration;

        if (!buffered?.length || !duration) {
            return;
        }

        const percent = (buffered.end(buffered.length - 1) / duration).toString();
        this.timelineBuffered.nativeElement.style.setProperty('--buffered-position', percent);
    }

    updateTimeLine(): void {
        const currentTime = this.currentSeconds();
        const timeline = this.getTimeline();
        const thumbIndicator = this.getThumbIndicator();

        if (timeline && thumbIndicator) {
            const percent = currentTime && this.video ? (currentTime / this.video.duration).toString() : '0';
            timeline.style.setProperty('--preview-position', percent);
            thumbIndicator.style.setProperty('--progress-position', percent);
        }
    }

    onVideoSpeedClick(speed: number): void {
        this.video!.playbackRate = speed;
    }

    onForward() {
        this.currentSecs = this.currentSecs + 5;
    }

    onBackward() {
        this.currentSecs = this.currentSecs - 5;
    }

    onLoad(): void {
        this.isVideoLoaded.set(true);
        this.video?.pause();
    }

    onSubtitleChange(subtitle: Subtitle | null) {
        for (let i = 0; i < this.video!.textTracks.length; i++) {
            const track = this.video!.textTracks[i];
            track.mode = +track.id === subtitle?.id ? 'showing' : 'hidden';
        }
    }

    get currentSecs(): number {
        return this.video?.currentTime ?? 0;
    }

    set currentSecs(value: number) {
        if (this.video) {
            const seconds = Math.min(Math.max(0, value), this.video.duration);
            if (isFinite(seconds)) {
                this.video.currentTime = seconds;
                this.currentSeconds.set(seconds);
            }
        }
    }
}
